import classNames from "classnames";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useSnapshot } from "valtio";
import { setIsAddCity, setIsAddZone, setIsAddCityAmount, setIsAddZoneAmount } from "../store";
import {
    store as device,
    setIsEdited,
    setActiveSubTab,
} from "../GeneralConfigC&C/City/store";
import { store as zoneStore, setIsEdited as setIsEditedZone } from "../GeneralConfigC&C/Zone/store";
import { store as cityAmountStore, setIsEdited as setIsEditedCityAmount } from "../GeneralConfigC&C/CityAmount/store";
import { store as zoneAmountStore, setIsEdited as setIsEditedZoneAmount } from "../GeneralConfigC&C/ZoneAmount/store";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";
import { templateStore } from "@pages/Applications/C&C/C&CSetup/templateStore";
import "./index.css";
import { getTabs } from "./hooks";
import { useSelector } from "react-redux";
interface ModalContentWrapperProps {
    selectedRows: any;
}
export function ModalContentWrapper({
    selectedRows,
}: ModalContentWrapperProps): JSX.Element {
    const { t } = useTranslation();
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );
    const { isEdited, activeSubTab } = useSnapshot(device);
    const { isEdited: isEditedZone } = useSnapshot(zoneStore);
    const { isEdited: isEditedCityAmount } = useSnapshot(cityAmountStore);
    const { isEdited: isEditedZoneAmount } = useSnapshot(zoneAmountStore);
    const { template, isLoading } = useSnapshot(templateStore);

    const anyFormEdited = isEdited || isEditedZone || isEditedCityAmount || isEditedZoneAmount;

    const [activeTab, setActiveTab] = React.useState<number>(0);
    const [openSubmenuTab, setOpenSubmenuTab] = React.useState<number | null>(
        0
    );
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const pendingActionRef = React.useRef<() => void>(() => {});

    const tabs = useMemo(() => {
        return getTabs({ selectedRows, isLoading, template, t });
    }, [selectedRows, isLoading, template, t]);

    const resetAllForms = React.useCallback(() => {
        setIsEdited(false);
        setIsEditedZone(false);
        setIsEditedCityAmount(false);
        setIsEditedZoneAmount(false);
        setIsAddCity(false);
        setIsAddZone(false);
        setIsAddCityAmount(false);
        setIsAddZoneAmount(false);
    }, []);

    const restoreAllEdited = React.useCallback((value: boolean) => {
        if (isEdited) setIsEdited(value);
        if (isEditedZone) setIsEditedZone(value);
        if (isEditedCityAmount) setIsEditedCityAmount(value);
        if (isEditedZoneAmount) setIsEditedZoneAmount(value);
    }, [isEdited, isEditedZone, isEditedCityAmount, isEditedZoneAmount]);

    function handleTabClick(index: number) {
        if (anyFormEdited) {
            pendingActionRef.current = () => {
                resetAllForms();
                performTabChange(index);
            };
            setShowConfirmation(true);
        } else {
            resetAllForms();
            performTabChange(index);
        }
    }

    const performTabChange = (index: number) => {
        const clickedTabHasSubmenu = tabs[index].subMenu;
        if (activeTab !== index) {
            setActiveTab(index);
            if (clickedTabHasSubmenu) {
                setOpenSubmenuTab(index);
                setActiveSubTab(0);
            } else {
                setOpenSubmenuTab(null);
            }
        } else if (clickedTabHasSubmenu) {
            setOpenSubmenuTab(index);
        }
    };

    const handleSubmenuClick = React.useCallback(
        (subMenuIndex: number) => {
            if (anyFormEdited) {
                pendingActionRef.current = () => {
                    resetAllForms();
                    setActiveSubTab(subMenuIndex);
                };
                setShowConfirmation(true);
            } else {
                resetAllForms();
                setActiveSubTab(subMenuIndex);
            }
        },
        [anyFormEdited, resetAllForms]
    );

    return (
        <div
            className="d-grid__clz h-100"
            style={{ gridTemplateColumns: "220px 1fr" }}
        >
            <Nav
                tabs
                className="d-flex flex-column"
                style={{ backgroundColor: mainColor }}
            >
                {tabs.map((tab, index) => (
                    <NavItem
                        key={index}
                        className={classNames("pb-1", tab.customClassName)}
                    >
                        <NavLink
                            className={classNames(
                                "border-0 menu-wrapper-nav-link__clz",
                                {
                                    "active active__clz": activeTab === index,
                                }
                            )}
                            onClick={() => handleTabClick(index)}
                        >
                            <span className="px-2">{tab.title}</span>
                        </NavLink>
                        {openSubmenuTab === index &&
                            tab.subMenu?.map((subTab, subMenuIndex) => (
                                <NavLink
                                    key={subMenuIndex}
                                    className={classNames(
                                        "border-0 text-nowrap pl-5 menu-wrapper-nav-link__clz",
                                        {
                                            "active sub-menu-wrapper-items-nav-link-active__clz":
                                                activeSubTab === subMenuIndex,
                                        }
                                    )}
                                    onClick={() =>
                                        handleSubmenuClick(subMenuIndex)
                                    }
                                >
                                    <span>{subTab.title}</span>
                                </NavLink>
                            ))}
                    </NavItem>
                ))}
            </Nav>

            <TabContent
                activeTab={activeTab}
                style={{
                    overflow: "hidden",
                    overflowY: "auto",
                    position: "revert",
                }}
            >
                {tabs.map((tab, index) => {
                    if (tab.subMenu && openSubmenuTab === index) {
                        return tab.subMenu.map(({ content }, subMenuIndex) => (
                            <TabPane
                                tabId={index}
                                className={classNames({
                                    "d-none": subMenuIndex !== activeSubTab,
                                })}
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    overflow: "hidden",
                                }}
                                key={subMenuIndex}
                            >
                                {content}
                            </TabPane>
                        ));
                    }
                    return (
                        <TabPane
                            tabId={index}
                            key={index}
                            className={classNames("h-100", {
                                "design-active-tab__clz": activeTab === 3,
                            })}
                        >
                            {tab.content}
                        </TabPane>
                    );
                })}
            </TabContent>

            {showConfirmation && (
                <ConfirmationUpdatingDataMessageModal
                    isModalOpened={showConfirmation}
                    setIsModalOpened={setShowConfirmation}
                    validationAction={() => setShowConfirmation(false)}
                    actions={() => pendingActionRef.current()}
                    setIsEdited={restoreAllEdited}
                />
            )}
        </div>
    );
}
